import React, { useCallback, useEffect, useState } from "react"
import { useStyletron } from "baseui"
import Add from "~/components/Icons/Add"
import useDesignEditorPages from "~/hooks/useDesignEditorScenes"
import { DesignEditorContext } from "~/contexts/DesignEditor"
import { nanoid } from "nanoid"
import { getDefaultTemplate } from "~/constants/design-editor"
import { useEditor, useFrame } from "@layerhub-io/react"
import { IScene } from "@layerhub-io/types"
import { DndContext, closestCenter, PointerSensor, useSensor, DragOverlay } from "@dnd-kit/core"
import { arrayMove, SortableContext, horizontalListSortingStrategy } from "@dnd-kit/sortable"
import { restrictToFirstScrollableAncestor, restrictToHorizontalAxis } from "@dnd-kit/modifiers"
import SceneItem from "./SceneItem"
import { Block } from "baseui/block"
import useContextMenuTimelineRequest from "~/hooks/useContextMenuTimelineRequest"
import SceneContextMenu from "./SceneContextMenu"

import { createClient } from "@supabase/supabase-js";
import Cookies from 'js-cookie';
import formatSizes from "~/constants/format-sizes"
import { exampleTemplate, layFlatCoverTemplate, makeCoverScene } from "~/constants/templates/exampleTemplate"
import { getLayflatSpineWidthMm, getSpineWidthMm } from "~/config/printingConfig"
import { loadVideoEditorAssets } from "~/utils/video"
import { loadTemplateFonts } from "~/utils/fonts"
import { IDesign } from "~/interfaces/DesignEditor"
import useAppContext from "~/hooks/useAppContext"
import { scaleTemplate } from "~/utils/layouts"
import { setEnvCookie } from "~/utils/env"
import { resolveSceneFrame } from "~/utils/sceneFrame"
import { getEditorGuideConfigs } from "~/utils/editorGuides"

const getPhotobookSku = () => (Cookies.get('photobookSku') || "").toUpperCase()

const isPersonalizedHardCover = () => {
  const sku = getPhotobookSku()
  const coverFinish = (Cookies.get('cover_finish') || "").toLowerCase()
  return sku.includes("PH") || coverFinish.includes("personalized") || coverFinish.includes("personalised")
}

const getCoverFormatEntry = () => {
  const sku = getPhotobookSku()
  return (
    formatSizes.find((f) => f.sku.some((s) => sku === s)) ||
    formatSizes.find((f) => f.sku.some((s) => sku.startsWith(s)))
  )
}

const buildWraparoundCoverScene = (sceneId?: string, insidePageCount?: number) => {
  const formatEntry = getCoverFormatEntry()
  const coverSizeData = formatEntry?.coverSize

  if (coverSizeData?.pixels) {
    // Derive the dynamic spine from the page count lookup table.
    // Fall back to the hardcoded spine from format-sizes if no page count is provided.
    let spineVal = coverSizeData.spine ?? 0
    if (insidePageCount != null && insidePageCount > 0) {
      const sku = getPhotobookSku()
      const newSpineMm = sku.includes("LAY")
        ? getLayflatSpineWidthMm(insidePageCount)
        : getSpineWidthMm(insidePageCount)

      // Compute the current (hardcoded) spine in mm so we can derive the fixed cover width.
      const currentSpineMm = typeof coverSizeData.spine === 'number'
        ? coverSizeData.spine
        : ((coverSizeData.spine as any)?.width ?? 0) / 300 * 25.4

      // fixed width = everything except the spine (bleed + creases + both cover panels)
      const fixedWidthMm = coverSizeData.width - currentSpineMm
      const dynamicWidthMm = fixedWidthMm + newSpineMm
      const dynamicWidthPx = Math.round(dynamicWidthMm / 25.4 * 300)

      return makeCoverScene(
        { width: dynamicWidthPx, height: coverSizeData.pixels.height },
        coverSizeData.bleed ?? 3,
        newSpineMm,
        sceneId || nanoid()
      )
    }

    return makeCoverScene(
      coverSizeData.pixels,
      coverSizeData.bleed ?? 3,
      spineVal,
      sceneId || nanoid()
    )
  }

  console.warn('coverSize not found in format-sizes for SKU:', getPhotobookSku(), '— falling back to layFlatCoverTemplate')
  return {
    ...layFlatCoverTemplate,
    id: sceneId || nanoid(),
    layers: JSON.parse(JSON.stringify(layFlatCoverTemplate.layers))
  }
}

const COVER_GEOMETRY_TOLERANCE = 2

const getSceneFrameSize = (scene: any) => {
  const backgroundLayer = scene?.layers?.find((layer: any) => layer?.type === 'Background')

  return {
    width: Number(scene?.frame?.width || backgroundLayer?.width || 0),
    height: Number(scene?.frame?.height || backgroundLayer?.height || 0),
  }
}

const getRenderedRect = (layer: any) => ({
  left: Number(layer?.left || 0),
  top: Number(layer?.top || 0),
  width: Number(layer?.width || 0) * Number(layer?.scaleX ?? 1),
  height: Number(layer?.height || 0) * Number(layer?.scaleY ?? 1),
})

const isCloseEnough = (a: number, b: number, tolerance = COVER_GEOMETRY_TOLERANCE) =>
  Math.abs(a - b) <= tolerance

const rectMatches = (actual: ReturnType<typeof getRenderedRect>, expected: ReturnType<typeof getRenderedRect>) =>
  isCloseEnough(actual.left, expected.left) &&
  isCloseEnough(actual.top, expected.top) &&
  isCloseEnough(actual.width, expected.width) &&
  isCloseEnough(actual.height, expected.height)

const getCoverLayerKey = (layer: any) => {
  if (!layer) return null
  if (layer.type === 'Background') return 'background'
  if (layer?.metadata?.coverRegion === 'back' || layer?.name === 'Back Cover Photo') return 'back'
  if (layer?.metadata?.coverRegion === 'front' || layer?.name === 'Front Cover Photo') return 'front'
  if (layer?.name === 'Spine Text') return 'spine'
  return null
}

const findCoverLayer = (scene: any, key: 'background' | 'back' | 'front' | 'spine') =>
  scene?.layers?.find((layer: any) => getCoverLayerKey(layer) === key)

const doesCoverSceneNeedNormalization = (scene: any, expectedScene: any) => {
  const currentFrame = getSceneFrameSize(scene)
  const expectedFrame = getSceneFrameSize(expectedScene)

  if (
    !isCloseEnough(currentFrame.width, expectedFrame.width) ||
    !isCloseEnough(currentFrame.height, expectedFrame.height)
  ) {
    return true
  }

  const requiredKeys: Array<'background' | 'back' | 'front'> = ['background', 'back', 'front']
  for (const key of requiredKeys) {
    const currentLayer = findCoverLayer(scene, key)
    const expectedLayer = findCoverLayer(expectedScene, key)

    if (!currentLayer || !expectedLayer) {
      return true
    }

    if (!rectMatches(getRenderedRect(currentLayer), getRenderedRect(expectedLayer))) {
      return true
    }
  }

  const expectedSpineLayer = findCoverLayer(expectedScene, 'spine')
  const currentSpineLayer = findCoverLayer(scene, 'spine')

  if (!!expectedSpineLayer !== !!currentSpineLayer) {
    return true
  }

  if (expectedSpineLayer && currentSpineLayer) {
    if (
      !isCloseEnough(Number(currentSpineLayer.left || 0), Number(expectedSpineLayer.left || 0)) ||
      !isCloseEnough(Number(currentSpineLayer.top || 0), Number(expectedSpineLayer.top || 0))
    ) {
      return true
    }
  }

  return false
}

const scaleLayerBetweenFrames = (
  layer: any,
  sourceFrame: { width: number; height: number },
  targetFrame: { width: number; height: number }
) => {
  if (!sourceFrame.width || !sourceFrame.height) {
    return layer
  }

  const scaleX = targetFrame.width / sourceFrame.width
  const scaleY = targetFrame.height / sourceFrame.height

  if (layer.type === 'Background') {
    return {
      ...layer,
      left: 0,
      top: 0,
      width: targetFrame.width,
      height: targetFrame.height,
      scaleX: 1,
      scaleY: 1,
    }
  }

  if (layer.type === 'StaticText') {
    const uniformScale = Math.min(scaleX, scaleY)
    return {
      ...layer,
      left: Number(layer.left || 0) * scaleX,
      top: Number(layer.top || 0) * scaleY,
      scaleX: Number(layer.scaleX ?? 1) * uniformScale,
      scaleY: Number(layer.scaleY ?? 1) * uniformScale,
    }
  }

  return {
    ...layer,
    left: Number(layer.left || 0) * scaleX,
    top: Number(layer.top || 0) * scaleY,
    scaleX: Number(layer.scaleX ?? 1) * scaleX,
    scaleY: Number(layer.scaleY ?? 1) * scaleY,
  }
}

const remapImageLayerToTarget = (layer: any, targetLayer: any) => {
  if (!layer) return targetLayer

  const baseWidth = Number(layer.width || targetLayer.width || 1)
  const baseHeight = Number(layer.height || targetLayer.height || 1)

  return {
    ...layer,
    id: layer.id || targetLayer.id,
    name: targetLayer.name,
    angle: targetLayer.angle,
    left: targetLayer.left,
    top: targetLayer.top,
    originX: targetLayer.originX,
    originY: targetLayer.originY,
    stroke: null,
    strokeWidth: 0,
    scaleX: targetLayer.width / baseWidth,
    scaleY: targetLayer.height / baseHeight,
    metadata: {
      ...layer.metadata,
      ...targetLayer.metadata,
    },
  }
}

const remapSpineLayerToTarget = (layer: any, targetLayer: any) => {
  if (!layer) return targetLayer

  return {
    ...layer,
    id: layer.id || targetLayer.id,
    name: targetLayer.name,
    angle: targetLayer.angle,
    left: targetLayer.left,
    top: targetLayer.top,
    width: targetLayer.width,
    height: targetLayer.height,
    originX: targetLayer.originX,
    originY: targetLayer.originY,
    scaleX: targetLayer.scaleX,
    scaleY: targetLayer.scaleY,
    fontSize: targetLayer.fontSize,
    metadata: {
      ...targetLayer.metadata,
      ...layer.metadata,
    },
  }
}

const normalizeWraparoundCoverScene = (scene: any, insidePageCount?: number) => {
  const expectedScene = buildWraparoundCoverScene(scene?.id, insidePageCount)
  const sanitizedLayers = (scene?.layers || []).map((layer: any) => {
    if (layer?.metadata?.isDropTarget && layer?.metadata?.coverRegion) {
      return {
        ...layer,
        stroke: null,
        strokeWidth: 0,
      }
    }

    return layer
  })

  if (!doesCoverSceneNeedNormalization(scene, expectedScene)) {
    return {
      ...scene,
      name: 'Cover',
      skipScaling: true,
      frame: expectedScene.frame,
      layers: sanitizedLayers,
    }
  }

  const sourceFrame = getSceneFrameSize(scene)
  const targetFrame = getSceneFrameSize(expectedScene)

  const structuralKeys = new Set(['background', 'back', 'front', 'spine'])
  const extraLayers = (scene?.layers || [])
    .filter((layer: any) => !structuralKeys.has(getCoverLayerKey(layer) || ''))
    .map((layer: any) => scaleLayerBetweenFrames(layer, sourceFrame, targetFrame))

  const backgroundLayer = findCoverLayer(scene, 'background')
  const expectedBackgroundLayer = findCoverLayer(expectedScene, 'background')
  const backLayer = findCoverLayer(scene, 'back')
  const expectedBackLayer = findCoverLayer(expectedScene, 'back')
  const frontLayer = findCoverLayer(scene, 'front')
  const expectedFrontLayer = findCoverLayer(expectedScene, 'front')
  const spineLayer = findCoverLayer(scene, 'spine')
  const expectedSpineLayer = findCoverLayer(expectedScene, 'spine')

  const normalizedLayers = [
    backgroundLayer
      ? {
          ...backgroundLayer,
          id: backgroundLayer.id || expectedBackgroundLayer?.id,
          name: expectedBackgroundLayer?.name || backgroundLayer.name,
          left: 0,
          top: 0,
          width: targetFrame.width,
          height: targetFrame.height,
          scaleX: 1,
          scaleY: 1,
        }
      : expectedBackgroundLayer,
    remapImageLayerToTarget(backLayer, expectedBackLayer),
    ...(expectedSpineLayer ? [remapSpineLayerToTarget(spineLayer, expectedSpineLayer)] : []),
    remapImageLayerToTarget(frontLayer, expectedFrontLayer),
    ...extraLayers,
  ].filter(Boolean)

  console.log('Normalizing wraparound cover scene for SKU:', getPhotobookSku(), {
    sourceFrame,
    targetFrame,
    sceneId: scene?.id,
  })

  return {
    ...scene,
    ...expectedScene,
    id: scene?.id || expectedScene.id,
    layers: normalizedLayers,
  }
}

const getInitialSpreadCount = (skuData: any) => {
  const standardPageSize = Number(skuData?.standard_page_size || 0)
  const totalSpreadsPerBook = Number(skuData?.total_spreads_per_book || 0)
  return standardPageSize > 0 ? standardPageSize : totalSpreadsPerBook
}

const hasRenderableScenes = (design: any) =>
  Array.isArray(design?.scenes) && design.scenes.length > 0

/**
 * Scale down a scene to a small thumbnail size before rendering a preview.
 * This prevents the browser from hanging on large-format products (e.g. A3
 * Layflat at 10000+ px wide) by rendering previews at ~400px instead of full
 * print resolution.
 */
const PREVIEW_MAX_WIDTH = 400

const createPreviewScene = (scene: IScene): IScene => {
  const frame = resolveSceneFrame(scene)
  if (frame.width <= PREVIEW_MAX_WIDTH) {
    return {
      ...scene,
      frame,
    } as IScene
  }

  const scale = PREVIEW_MAX_WIDTH / frame.width
  const newFrame = {
    width: Math.round(frame.width * scale),
    height: Math.round(frame.height * scale),
  }

  const scaledLayers = scene.layers.map((layer: any) => {
    if (layer.type === 'Background') {
      return {
        ...layer,
        width: newFrame.width,
        height: newFrame.height,
        left: 0,
        top: 0,
        scaleX: 1,
        scaleY: 1,
      }
    }
    return {
      ...layer,
      left: (layer.left || 0) * scale,
      top: (layer.top || 0) * scale,
      scaleX: (layer.scaleX || 1) * scale,
      scaleY: (layer.scaleY || 1) * scale,
    }
  })

  return {
    ...scene,
    frame: newFrame,
    layers: scaledLayers,
  } as IScene
}

const getSceneSyncSignature = (scene?: IScene | null) => {
  if (!scene) return ""

  return JSON.stringify({
    id: scene.id,
    name: scene.name || "",
    frame: resolveSceneFrame(scene),
    layers: scene.layers || [],
  })
}

export default function () {
  const scenes = useDesignEditorPages()
  const { setScenes, setCurrentScene, currentScene, setCurrentDesign, currentDesign } =
    React.useContext(DesignEditorContext)
  const editor = useEditor()
  const [css] = useStyletron()
  const [currentPreview, setCurrentPreview] = React.useState("")
  const frame = useFrame()
  const [draggedScene, setDraggedScene] = React.useState<IScene | null>(null)
  const lastImportedSceneSignatureRef = React.useRef<string>("")
  const contextMenuTimelineRequest = useContextMenuTimelineRequest()
  const { skuData } = useAppContext()

  // URL cleanup is handled by Container.tsx via history.replaceState
  // so no redirect is needed here.


  const [cart, setCart] = useState([]);
  const [size, setSize] = useState<any>({ "width": 3570, "height": 2546 })
  //setCurrentDesign({"name": "22"})

  const configureEditorGuides = React.useCallback(
    (scene: IScene, availableScenes: IScene[]) => {
      if (!editor || !scene) return

      const { bleedGuideConfig, safeZoneConfig } = getEditorGuideConfigs({
        sku: getPhotobookSku(),
        coverFinish: Cookies.get('cover_finish') || "",
        scenes: availableScenes as any[],
        scene: scene as any,
        frame: resolveSceneFrame(scene),
      })

      ;(editor as any).bleedGuides?.setConfig(bleedGuideConfig)
      ;(editor as any).safeZone?.setConfig(safeZoneConfig)
    },
    [editor]
  )

  useEffect(() => {
    getSkuAndSize();

  }, []);

  async function getSkuAndSize() {
    // get sku from cookie
    const sku = Cookies.get('photobookSku');

    const dimensions = formatSizes.find(x => x.sku.includes(sku))
    console.log("Dimensions", dimensions)
    if (!sku || !dimensions) {
      //window.location.href = "https://memoryboxco.co.za";
    }

    if (dimensions) {
      setSize(dimensions.pixels)
    }
  }


  const sensors = [
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 5,
      },
    }),
  ]

  React.useEffect(() => {
    if (editor && scenes && currentScene) {
      const isCurrentSceneLoaded = scenes.find((s) => s.id === currentScene?.id)
      if (!isCurrentSceneLoaded) {
        setCurrentScene(scenes[0])
      }
    }
  }, [editor, scenes, currentScene])

  React.useEffect(() => {
    let watcher = async () => {
      const updatedTemplate = editor.scene.exportToJSON()
      const previewScene = createPreviewScene(updatedTemplate)
      const updatedPreview = (await editor.renderer.render(previewScene)) as string
      setCurrentPreview(updatedPreview)
      //setCurrentScene(updatedTemplate)
    }
    if (editor) {
      editor.on("history:changed", watcher)
    }
    return () => {
      if (editor) {
        editor.off("history:changed", watcher)
      }
    }
  }, [editor])

  React.useEffect(() => {
    let cancelled = false

    const loadTemplate = async () => {
      if (editor) {
        if (currentScene) {
          const currentSceneSignature = getSceneSyncSignature(currentScene)
          if (currentSceneSignature && currentSceneSignature === lastImportedSceneSignatureRef.current) {
            return
          }
          console.log("Updating current scene:", currentScene.id)
          updateCurrentScene(currentScene, scenes as any, currentSceneSignature)
        } else {
          const sku = getPhotobookSku()
          const needsSkuBootstrap = !!sku && !skuData && !Cookies.get('activeDesignId')

          if (needsSkuBootstrap) {
            console.log("Waiting for skuData before creating template for SKU:", sku)
            return
          }

          console.log("Loading initial template...")
          let template
          const designTemplate = await loadSavedTemplateOrDefault()

          if (cancelled || !designTemplate) {
            return
          }

          console.log("Design template loaded:", {
            id: designTemplate?.id,
            scenesCount: designTemplate?.scenes?.length || 0,
            frame: designTemplate?.frame,
            firstSceneLayers: designTemplate?.scenes?.[0]?.layers?.length || 0
          })

          if (!designTemplate || !designTemplate.scenes || designTemplate.scenes.length === 0) {
            console.error("Invalid design template - no scenes found")
            return
          }

          // In download mode, use the design's original frame dimensions instead of scaling
          // This preserves the correct size from the saved project
          const isDownloadMode = Cookies.get('isDownload') === 'true'
          const targetSize = isDownloadMode && designTemplate.frame
            ? designTemplate.frame
            : size

          // ─── Cover scene normalization ───────────────────────────────────────────
          // For personalised covers, scene 0 is always the cover, even in older
          // projects where it may still look like a plain inside spread. Always
          // normalize that scene back to the wraparound cover geometry so it uses
          // the correct cover frame instead of the inner-page frame.
          const firstScene = designTemplate.scenes?.[0] as any
          if (isPersonalizedHardCover() && firstScene) {
            // scenes[0] is the cover; everything after are inside pages
            const insidePageCount = (designTemplate.scenes?.length ?? 1) - 1
            designTemplate.scenes[0] = normalizeWraparoundCoverScene(firstScene, insidePageCount)
          }
          // ─────────────────────────────────────────────────────────────────────────

          console.log("Using size for scaling:", targetSize, "isDownloadMode:", isDownloadMode)

          // scaleTemplate scales inner-page scenes uniformly to the target size.
          // Cover scenes (name === 'Cover' or skipScaling) are passed through unchanged.
          const scaledTemplate: any = scaleTemplate(designTemplate, targetSize)
          template = await loadGraphicTemplate(scaledTemplate)

          if (cancelled) {
            return
          }


          console.log("Template after processing:", {
            scenesCount: template.scenes?.length || 0,
            firstScenePreview: template.scenes?.[0]?.preview ? "exists" : "missing"
          })

          // Set scenes first
          setScenes(template.scenes)
          setCurrentDesign(template.design)

          // Select the first scene and let the normal currentScene load path
          // import it once with the correct guide/safe-zone configuration.
          if (template.scenes[0]) {
            lastImportedSceneSignatureRef.current = ""
            setCurrentScene(template.scenes[0])
          }
        }
      }
    }

    loadTemplate().catch(console.error)
    return () => {
      cancelled = true
    }
  }, [editor, currentScene, skuData])

  const loadSavedTemplateOrDefault = async () => {
    // Prefer localStorage (same-origin, reliable) over cookies which can have
    // stale values from a more specific subdomain the browser prioritises.
    let designId = localStorage.getItem('activeDesignId') || Cookies.get('activeDesignId')
    const storedDesigns = ['savedProject', 'currentSessionDesign']

    for (const storageKey of storedDesigns) {
      const project = localStorage.getItem(storageKey)
      if (project) {
        const projectDesign = JSON.parse(project)
        const matchesActiveDesign = designId && projectDesign?.id === designId

        if (matchesActiveDesign && hasRenderableScenes(projectDesign)) {
          if (storageKey === 'currentSessionDesign') {
            setEnvCookie('activeDesignId', projectDesign.id)
          }
          console.log(`Loading ${storageKey} with`, projectDesign.scenes.length, "scenes")
          return projectDesign
        }

        console.warn(`Ignoring ${storageKey} because it does not match the active design or has no scenes`)
      }
    }

    // Create new project if no design ID or no project found
    console.log("Creating new project template")
    if (getInitialSpreadCount(skuData) <= 0) {
      console.log("Waiting for SKU data before generating a new template")
      return null
    }

    designId = designId && designId !== "" ? designId : nanoid()
    setEnvCookie('activeDesignId', designId)
    const freshTemplate = JSON.parse(JSON.stringify(exampleTemplate))
    freshTemplate.id = designId

    const initialSpreadCount = getInitialSpreadCount(skuData)

    // Build the starting spread count from product metadata.
    // Some newer products store 0 in standard_page_size and rely on total_spreads_per_book.
    if (initialSpreadCount > 0) {
      // Check if it's Layflat based on SKU
      const sku = Cookies.get('photobookSku') || ""
      const isLayflat = sku.includes("LAY")

      const hasWraparoundCover = isPersonalizedHardCover()

      // Both Layflat and Standard covers are always single spreads if they are personalized hard covers.
      // Therefore, if it is personalized, we add the layFlatCoverTemplate (which is a spread).
      const newScenes: any[] = []

      if (hasWraparoundCover) {
        newScenes.push(buildWraparoundCoverScene(undefined, initialSpreadCount))
      }

      // Scene 1 is the inside page template
      // If the template has index 1, use it, else fallback to index 0.
      const insidePageTemplate = freshTemplate.scenes[1] || freshTemplate.scenes[0]

      // Generate scenes based on standard_page_size
      for (let i = 0; i < initialSpreadCount; i++) {
        // Deep clone the layers to avoid reference sharing and ensure valid new state
        const clonedLayers = insidePageTemplate.layers ? JSON.parse(JSON.stringify(insidePageTemplate.layers)) : []

        newScenes.push({
          ...insidePageTemplate,
          id: nanoid(),
          name: `Page ${i + 1}`,
          layers: clonedLayers
        })
      }

      freshTemplate.scenes = newScenes
    }

    if (isPersonalizedHardCover() && freshTemplate.scenes?.[0]) {
      // scenes[0] is the cover; everything after are inside pages
      const insidePageCount = freshTemplate.scenes.length - 1
      freshTemplate.scenes[0] = normalizeWraparoundCoverScene(freshTemplate.scenes[0], insidePageCount)
    }
    return freshTemplate;
  }



  const loadGraphicTemplate = async (payload: IDesign) => {
    const scenes = []
    const { scenes: scns, ...design } = payload

    for (let i = 0; i < scns.length; i++) {
      const scn = scns[i]
      // Use the scene's own frame if it has one (e.g. layflat cover scene uses
      // coverSize.pixels while inner pages use payload.frame / page size)
      const sceneFrame = (scn as any).frame || payload.frame
      const scene: IScene = {
        name: scn.name,
        frame: sceneFrame,
        id: scn.id,
        layers: scn.layers,
        metadata: {},
      }

      // Only load assets for the first scene immediately
      if (i === 0) {
        const loadedScene = await loadVideoEditorAssets(scene)
        await loadTemplateFonts(loadedScene)
        // Generate a scaled-down preview to avoid hanging on large formats
        let initialPreview = scn.preview || ""
        scenes.push({ ...loadedScene, preview: initialPreview, loaded: true } as any)
      } else {
        // For other scenes, push them as-is (unloaded)
        // We'll load assets in the background
        scenes.push({ ...scene, preview: scn.preview || "", loaded: false } as any)
      }
    }

    return { scenes, design }
  }


  // Background loader: Load assets AND generate previews for scenes
  React.useEffect(() => {
    let active = true;
    const processScene = async () => {
      if (!editor || !scenes) return;

      // Priority 1: Find a scene that hasn't loaded its assets (fonts, video frames)
      const sceneToLoadIndex = scenes.findIndex((s: any) => !s.loaded);

      if (sceneToLoadIndex !== -1) {
        const scene = scenes[sceneToLoadIndex];
        try {
          // Load assets for this scene
          const loadedScene = await loadVideoEditorAssets(scene)
          await loadTemplateFonts(loadedScene)

          if (active) {
            setScenes(prev => {
              const newScenes = [...prev];
              // Mark as loaded, keeping existing preview if any
              newScenes[sceneToLoadIndex] = { ...loadedScene, preview: scene.preview, loaded: true } as any;
              return newScenes;
            });
          }
        } catch (err) {
          console.error("Failed to load assets for scene", scene.id, err);
          // Mark as loaded even on error to prevent infinite loop
          if (active) {
            setScenes(prev => {
              const newScenes = [...prev];
              newScenes[sceneToLoadIndex] = { ...scene, loaded: true } as any;
              return newScenes;
            });
          }
        }
        return; // Exit to let state update and effect re-run
      }


      // Priority 2: Find a scene that has loaded assets but needs a preview
      const sceneToPreviewIndex = scenes.findIndex((s: any) => s.loaded && !s.preview);

      if (sceneToPreviewIndex !== -1) {
        const scene = scenes[sceneToPreviewIndex];
        try {
          // Generate a lower quality/resolution preview for the thumbnail
          // Scale down the scene before rendering to avoid hanging on large formats
          const previewScene = createPreviewScene(scene)
          const preview = await editor.renderer.render(previewScene, {
            quality: 0.1,
            format: "jpeg"
          });

          if (active) {
            setScenes(prev => {
              const newScenes = [...prev];
              newScenes[sceneToPreviewIndex] = { ...scene, preview: preview as string };
              return newScenes;
            });
          }
        } catch (err) {
          console.error("Failed to generate preview for scene", scene.id, err);
        }
      }
    };

    // Use a small timeout to allow UI to breathe
    const timeoutId = setTimeout(() => {
      processScene();
    }, 50);

    return () => {
      active = false;
      clearTimeout(timeoutId);
    }
  }, [editor, scenes]);

  const updateCurrentScene = React.useCallback(
    async (design: IScene, availableScenes: IScene[] = [], sceneSignature?: string) => {
      console.log("updateCurrentScene called for scene:", design.id, "with", design.layers?.length, "layers")
      try {
        configureEditorGuides(design, availableScenes)
        const importResult = await editor.scene.importFromJSON(design)
        const loadedScene = importResult?.scene || design
        lastImportedSceneSignatureRef.current = sceneSignature || getSceneSyncSignature(loadedScene)
        const previewScene = createPreviewScene(loadedScene)
        const updatedPreview = (await editor.renderer.render(previewScene)) as string
        setCurrentPreview(updatedPreview)

        if (importResult?.safeZoneAdjusted) {
          const correctedScene = {
            ...design,
            ...loadedScene,
            preview: updatedPreview,
          }

          lastImportedSceneSignatureRef.current = getSceneSyncSignature(correctedScene)
          setScenes((prevScenes: any[]) =>
            prevScenes.map((scene: any) => (scene.id === correctedScene.id ? { ...scene, ...correctedScene } : scene))
          )
          setCurrentScene((prevScene: any) =>
            prevScene?.id === correctedScene.id ? { ...prevScene, ...correctedScene } : prevScene
          )
        }

        console.log("Scene loaded successfully into canvas")
      } catch (error) {
        console.error("Error updating current scene:", error)
      }
    },
    [configureEditorGuides, editor]
  )

  const addScene = React.useCallback(async () => {
    setCurrentPreview("")
    const updatedTemplate = editor.scene.exportToJSON()
    const previewScene = createPreviewScene(updatedTemplate)
    const updatedPreview = await editor.renderer.render(previewScene)

    const updatedPages = scenes.map((p) => {
      if (p.id === updatedTemplate.id) {
        return { ...updatedTemplate, preview: updatedPreview }
      }
      return p
    })

    const defaultTemplate = getDefaultTemplate(currentDesign.frame)
    const newPreview = await editor.renderer.render(defaultTemplate)
    const newPage = { ...defaultTemplate, id: nanoid(), preview: newPreview } as any
    const items = [{
      ...defaultTemplate,
      id: nanoid(),
      preview: newPreview,
    } as any];

    // Since we removed separate Back Covers, we just append new pages to the end
    const newPages = [...updatedPages, ...items];

    setScenes(newPages)
    setCurrentScene(items[0] as any)

  }, [scenes, currentDesign])

  const changePage = React.useCallback(
    async (page: any) => {
      setCurrentPreview("")
      if (editor) {
        const updatedTemplate = editor.scene.exportToJSON()
        const previewScene = createPreviewScene(updatedTemplate)
        const updatedPreview = await editor.renderer.render(previewScene)

        const updatedPages = scenes.map((p) => {
          if (p.id === updatedTemplate.id) {
            return { ...updatedTemplate, preview: updatedPreview }
          }
          return p
        }) as any[]

        setScenes(updatedPages)
        setCurrentScene(page)
      }
    },
    [editor, scenes, currentScene]
  )

  function handleDragStart(event: any) {
    const draggedScene = scenes.find((s) => s.id === event.active.id)
    if (draggedScene) {
      setDraggedScene(draggedScene)
    }
  }

  function handleDragEnd(event: any) {
    const { active, over } = event

    if (active.id !== over.id) {
      setScenes((items) => {
        const oldIndex = items.findIndex((item) => item.id === active.id)
        const newIndex = items.findIndex((item) => item.id === over.id)

        return arrayMove(items, oldIndex, newIndex)
      })
    }
    setDraggedScene(null)
  }

  const hasCover = isPersonalizedHardCover()
  const coverOffset = hasCover ? 1 : 0

  function canAddMoreScenes() {
    return scenes.length < (skuData?.total_spreads_per_book || 0) + coverOffset + 1
  }

  return (

    /* <DndContext
      modifiers={[restrictToFirstScrollableAncestor, restrictToHorizontalAxis]}
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragEnd={handleDragEnd}
      onDragStart={handleDragStart}
      autoScroll={true}
    >
      
    </DndContext> */
    <Block
      id="TimelineItemsContainer"
      $style={{ padding: "0.25rem 0.75rem", background: "#ffffff", position: "relative", overflowX: "auto", width: "100%" }}
    >
      <div className={css({ display: "flex", alignItems: "center" })} id="AddScene">
        {contextMenuTimelineRequest.visible && <SceneContextMenu />}

        {scenes.map((page, index) => (
          <SceneItem
            key={index}
            isCurrentScene={page.id === currentScene?.id}
            scene={page}
            index={index}
            totalScenes={scenes.length}
            changePage={changePage}
            preview={
              currentPreview && page.id === currentScene?.id ? currentPreview : page.preview ? page.preview : ""
            }
          />
        ))}
        {
          !Cookies.get('isDownload')?.includes('true') && canAddMoreScenes() &&
          <div
            style={{
              background: "#ffffff",
              padding: "1rem 1rem 1rem 0.5rem",
            }}
          >
            <div
              onClick={addScene}
              className={css({
                width: "100px",
                height: "56px",
                background: "rgb(243,244,246)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                cursor: "pointer",
              })}
            >
              <Add size={20} />
            </div>
          </div>
        }

        {/* <SortableContext items={scenes} strategy={horizontalListSortingStrategy}>
            
            
          </SortableContext> */}
        {/* <DragOverlay>
            {draggedScene ? (
              <Block
                $style={{
                  backgroundImage: `url(${draggedScene.preview})`,
                  backgroundSize: `${frame ? (frame.width * 70) / frame.height : 70}px 70px`,
                  height: "80px",
                  opacity: 0.75,
                }}
              ></Block>
            ) : null}
          </DragOverlay> */}
      </div>
    </Block>
  )
}
